import Image from "next/image";
import { useState } from "react";
import Icon from "~/assets/icons/Icon";
import { useContext } from "react";
import Button from "~/components/Button";
import { CartContext } from "~/context/cart";
import { getFormattedImageAttributes } from "~/utils/getImageAttributes";
import { stringToCurrency } from "~/utils/stringConversion";
import { useRouter } from "next/router";
import { StoreCartItem } from "~/shared-types";
import { IconType } from "~/assets/icons/iconPaths";
import classNames from "classnames";
import { getOfferDetails } from "~/utils/getOfferDetails";
import { Badge } from "~/components/Backroom/Badge";
import { showToast } from "~/utils/toast";

type CartItemProps = {
  item: StoreCartItem;
};

type CartItemActionButtonProps = {
  isUpdating: boolean;
  onClick: () => void;
  iconName: IconType;
  title: string;
};

const CartItemActionButton = ({
  isUpdating,
  onClick,
  iconName,
  title,
}: CartItemActionButtonProps) => {
  return (
    <Button
      onClick={onClick}
      disabled={isUpdating}
      className="text-danger outline-none px-2 py-1 -mb-1 -mr-2 !shadow-none"
      title={title}
    >
      <Icon
        name={isUpdating ? "spinner" : iconName}
        className={classNames("w-6 h-6", isUpdating && "animate-spin")}
      />
    </Button>
  );
};

function CartItem({ item }: CartItemProps) {
  const { quantity } = item;
  const product = item.product.attributes;
  const store = product.store.data;
  const image = product.images.data[0];

  const [isUpdating, setIsUpdating] = useState(false);
  const [quantityValue, setQuantityValue] = useState(quantity);
  const { updateInCart, removeProductsFromCart } = useContext(CartContext);
  const router = useRouter();

  if (!store) return null;

  async function updateCartItem() {
    setIsUpdating(true);

    await updateInCart(item.product.id, quantityValue);
    await router.replace(router.asPath);

    setIsUpdating(false);
  }

  async function deleteCartItem() {
    setIsUpdating(true);

    await removeProductsFromCart([item.product.id]);
    await router.replace(router.asPath);

    setIsUpdating(false);
  }

  const onQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    if (value < 0) {
      setQuantityValue(0);
    } else {
      setQuantityValue(value);
    }
  };

  const onUniqueProductClick = () => {
    if (product.isUnique) {
      return showToast(
        "info",
        "Unikavare",
        "Du kan kun tilføje én unikavare til kurven"
      );
    }
  };

  const { offerPrice } = getOfferDetails(item.product);

  return (
    <li className="grid grid-cols-6 gap-2 sm:gap-5 mb-5 items-center">
      <Image
        className="rounded-lg col-span-1 w-full"
        {...getFormattedImageAttributes(image, "thumbnail")}
      />
      <div className="col-span-3">
        <p className="line-clamp-[1]">{product.name}</p>
        <div className="text-xs mt-1">
          <p>
            <span
              className={classNames(
                offerPrice && "line-through text-secondary-500"
              )}
            >
              {stringToCurrency({ amount: product.price })}
            </span>
            {offerPrice ? (
              <span className="ml-2">
                {stringToCurrency({ amount: offerPrice })}
              </span>
            ) : null}
          </p>
          {product.canOnlyBePickedUp ? (
            <Badge
              color="lightgray"
              className="text-xs text-secondary-600 italic mt-2"
            >
              Kan kun afhentes
            </Badge>
          ) : null}
        </div>
      </div>
      <div className="col-span-2 flex flex-row gap-2 ml-auto">
        <input
          placeholder="Antal"
          className="border border-secondary-200 rounded-lg py-3 text-center text-secondary-800 placeholder:text-secondary-400 bg-transparent min-w-0 shrink w-14"
          readOnly={product.isUnique}
          value={quantityValue}
          onChange={onQuantityChange}
          onClick={onUniqueProductClick}
        />
        {quantity !== quantityValue && quantityValue > 0 ? (
          <CartItemActionButton
            isUpdating={isUpdating}
            onClick={updateCartItem}
            iconName="spinner"
            title="Opdater antal"
          />
        ) : (
          <CartItemActionButton
            isUpdating={isUpdating}
            onClick={deleteCartItem}
            iconName="trash"
            title="Fjern vare fra kurven"
          />
        )}
      </div>
    </li>
  );
}

export { CartItem };
